import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import type { PersistQueryClientProviderProps } from "@tanstack/react-query-persist-client";

/**
 * Bump this whenever the persisted cache shape changes so stale snapshots from
 * older app versions are discarded instead of hydrated.
 */
const PERSIST_BUSTER = "v1";

const PERSIST_KEY = "cafe-pos-rq-cache";

/**
 * How long a persisted snapshot stays valid. Kept long so that after a device
 * reboot (especially offline) the app can hydrate the last known shift/branch
 * context instead of starting empty.
 */
const PERSIST_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Only persist small, critical queries. Large datasets (products, order lists)
 * already live in IndexedDB via the offline reference cache, and persisting
 * them here would risk blowing the localStorage quota.
 */
const PERSISTED_QUERY_PREFIXES: ReadonlyArray<ReadonlyArray<string>> = [
  ["pos", "shifts"],
  ["pos", "branches"],
];

function shouldPersistQueryKey(queryKey: readonly unknown[]): boolean {
  return PERSISTED_QUERY_PREFIXES.some((prefix) =>
    prefix.every((segment, index) => queryKey[index] === segment),
  );
}

export function createQueryPersister() {
  if (typeof window === "undefined") return undefined;
  return createSyncStoragePersister({
    storage: window.localStorage,
    key: PERSIST_KEY,
    throttleTime: 1000,
  });
}

export function createPersistOptions(): PersistQueryClientProviderProps["persistOptions"] {
  const persister = createQueryPersister();
  if (!persister) {
    // Fallback to a no-op persister when storage is unavailable.
    return {
      persister: {
        persistClient: async () => {},
        restoreClient: async () => undefined,
        removeClient: async () => {},
      },
    };
  }

  return {
    persister,
    maxAge: PERSIST_MAX_AGE_MS,
    buster: PERSIST_BUSTER,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        if (query.state.status !== "success") return false;
        return shouldPersistQueryKey(query.queryKey);
      },
    },
  };
}
